// サウンドエンジン（WebAudio）。BGM ループ + 効果音の再生を一手に引き受ける。
//
// 設計のねらい:
//  - ロジック（sim）からは完全に独立。main.ts がゲームイベントに合わせて play* を呼ぶ。
//  - **モバイルの自動再生制限**: AudioContext は最初のユーザー操作（pointerdown）で
//    resume する必要がある。unlock() を初回ポインタで一度だけ呼ぶ運用にする。
//  - 素材は CC0（BGM: OpenGameArt の Starfield Romance / SE: Kenney）。出典は docs/CREDITS.md。
//  - ブラウザ互換のため ogg / m4a の両方を用意し、再生できる方を選ぶ
//    （Chromium/WebView は ogg、Safari 系は m4a）。base './' ビルドでも解けるよう相対参照。
//
// すべての操作は「鳴らなくてもゲーム進行は止めない」方針で、失敗は握りつぶす。

/** 効果音の論理名（ファイル名と一致）。 */
export type SeName = 'place' | 'remove' | 'prune' | 'grow' | 'arrive' | 'tip' | 'tap';

const SE_NAMES: readonly SeName[] = ['place', 'remove', 'prune', 'grow', 'arrive', 'tip', 'tap'];

/**
 * BGM ファイルのベース名（拡張子は pickExt が決める）。
 * v0.5.1: ボサノヴァ調 cozy → アンビエント/スペース系（Starfield Romance, CC0）へ差し替え。
 */
const BGM_BASENAME = 'bgm_ambient';
/** BGM の音量（控えめ。アンビエントは音量を主張しないので少し抑える）。 */
const BGM_GAIN = 0.3;
/** SE の音量。 */
const SE_GAIN = 0.6;

/**
 * 再生可能な音声拡張子を選ぶ。Chromium/Android WebView は ogg、Safari は m4a。
 * 判定できない環境では ogg を既定にする（dev/Playwright は Chromium）。
 */
function pickExt(): 'ogg' | 'm4a' {
  try {
    const a = document.createElement('audio');
    if (a.canPlayType('audio/ogg; codecs="vorbis"')) return 'ogg';
    if (a.canPlayType('audio/mp4; codecs="mp4a.40.2"')) return 'm4a';
  } catch {
    /* SSR/テスト環境などでは ogg にフォールバック */
  }
  return 'ogg';
}

export interface AudioEngine {
  /** 初回ユーザー操作で呼ぶ。AudioContext を resume し、BGM を（ミュートでなければ）開始する。 */
  unlock(): void;
  /** 効果音を 1 つ鳴らす（ミュート中は何もしない）。 */
  playSe(name: SeName): void;
  /**
   * 長押し撤去の「ﾆｭﾆｭﾝ」コミカル音（v0.5.1）。WebAudio のオシレーターでピッチを
   * 揺らして合成する（素材ファイル不要）。撤去完了の「ポン」と消える瞬間に鳴らす。
   * ミュート中は何もしない。
   */
  playRemovalWobble(): void;
  /** ミュート状態を切り替える。BGM の停止/再開もここで行う。 */
  setMuted(muted: boolean): void;
  /** 現在ミュート中か。 */
  readonly muted: boolean;
}

/**
 * AudioEngine を生成する。生成時点では AudioContext を作らない（自動再生制限のため
 * ユーザー操作まで遅延する）。muted は初期値（セーブから復元した値）を渡す。
 */
export function createAudioEngine(initialMuted: boolean): AudioEngine {
  const ext = pickExt();
  let ctx: AudioContext | null = null;
  let masterSe: GainNode | null = null;
  let bgmGain: GainNode | null = null;
  let bgmSource: AudioBufferSourceNode | null = null;
  let bgmBuffer: AudioBuffer | null = null;
  const seBuffers = new Map<SeName, AudioBuffer>();
  let muted = initialMuted;
  let unlocked = false;

  /** AudioContext を初期化（一度だけ）。 */
  function ensureCtx(): AudioContext | null {
    if (ctx) return ctx;
    try {
      const Ctor: typeof AudioContext =
        window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      ctx = new Ctor();
      masterSe = ctx.createGain();
      masterSe.gain.value = SE_GAIN;
      masterSe.connect(ctx.destination);
      bgmGain = ctx.createGain();
      bgmGain.gain.value = muted ? 0 : BGM_GAIN;
      bgmGain.connect(ctx.destination);
    } catch {
      ctx = null;
    }
    return ctx;
  }

  /** URL から AudioBuffer をデコードして取り込む。失敗は null。 */
  async function loadBuffer(url: string): Promise<AudioBuffer | null> {
    const c = ensureCtx();
    if (!c) return null;
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const arr = await res.arrayBuffer();
      return await c.decodeAudioData(arr);
    } catch {
      return null;
    }
  }

  /** 全 SE を非同期で先読みする（来店時などに取りこぼさないため）。 */
  function preloadSe(): void {
    for (const name of SE_NAMES) {
      if (seBuffers.has(name)) continue;
      void loadBuffer(`./audio/se/${name}.${ext}`).then((buf) => {
        if (buf) seBuffers.set(name, buf);
      });
    }
  }

  /** BGM バッファを読み込んでループ再生を開始する（ミュート中は読み込みのみ）。 */
  function startBgm(): void {
    const c = ensureCtx();
    if (!c || !bgmGain) return;
    const begin = (): void => {
      if (!c || !bgmGain || !bgmBuffer || bgmSource) return;
      try {
        const src = c.createBufferSource();
        src.buffer = bgmBuffer;
        src.loop = true;
        src.connect(bgmGain);
        src.start();
        bgmSource = src;
      } catch {
        /* noop */
      }
    };
    if (bgmBuffer) {
      begin();
    } else {
      void loadBuffer(`./audio/${BGM_BASENAME}.${ext}`).then((buf) => {
        bgmBuffer = buf;
        if (buf && !muted) begin();
      });
    }
  }

  function unlock(): void {
    if (unlocked) return;
    unlocked = true;
    const c = ensureCtx();
    if (!c) return;
    if (c.state === 'suspended') void c.resume();
    preloadSe();
    if (!muted) startBgm();
  }

  function playSe(name: SeName): void {
    if (muted) return;
    const c = ensureCtx();
    if (!c || !masterSe) return;
    if (c.state === 'suspended') void c.resume();
    const buf = seBuffers.get(name);
    if (!buf) {
      // 未読込なら読み込んでから鳴らす（初回だけわずかに遅延）。
      void loadBuffer(`./audio/se/${name}.${ext}`).then((b) => {
        if (!b) return;
        seBuffers.set(name, b);
        if (!muted) playBuffer(b);
      });
      return;
    }
    playBuffer(buf);
  }

  function playBuffer(buf: AudioBuffer): void {
    if (!ctx || !masterSe) return;
    try {
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(masterSe);
      src.start();
    } catch {
      /* noop */
    }
  }

  /**
   * 「ﾆｭﾆｭﾝ」のコミカル音をオシレーターで合成する。三角波のピッチを下→上→下と
   * うねらせ、短いエンベロープでぷるんと鳴らす（撤去がポンと消える瞬間用）。
   */
  function playRemovalWobble(): void {
    if (muted) return;
    const c = ensureCtx();
    if (!c || !masterSe) return;
    if (c.state === 'suspended') void c.resume();
    try {
      const now = c.currentTime;
      const osc = c.createOscillator();
      osc.type = 'triangle';
      // ピッチのうねり: 中音→低→高→中とぐにゃっと曲げる（ﾆｭﾆｭﾝ）。
      osc.frequency.setValueAtTime(440, now);
      osc.frequency.linearRampToValueAtTime(300, now + 0.06);
      osc.frequency.linearRampToValueAtTime(620, now + 0.16);
      osc.frequency.linearRampToValueAtTime(380, now + 0.26);
      // 軽いビブラートを足して「ぷるぷる」感を出す。
      const lfo = c.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 22;
      const lfoGain = c.createGain();
      lfoGain.gain.value = 35;
      lfo.connect(lfoGain).connect(osc.frequency);
      // エンベロープ（ぷるんと立ち上がってすっと消える）。
      const env = c.createGain();
      env.gain.setValueAtTime(0.0001, now);
      env.gain.exponentialRampToValueAtTime(0.5, now + 0.03);
      env.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);
      osc.connect(env).connect(masterSe);
      osc.start(now);
      lfo.start(now);
      osc.stop(now + 0.34);
      lfo.stop(now + 0.34);
    } catch {
      /* noop */
    }
  }

  function setMuted(next: boolean): void {
    if (next === muted) return;
    muted = next;
    if (bgmGain && ctx) {
      // クリックノイズを避けて素早くフェード。
      const now = ctx.currentTime;
      bgmGain.gain.cancelScheduledValues(now);
      bgmGain.gain.setValueAtTime(bgmGain.gain.value, now);
      bgmGain.gain.linearRampToValueAtTime(muted ? 0 : BGM_GAIN, now + 0.18);
    }
    // ミュート解除時に（unlock 済みで）BGM がまだ始まっていなければ開始する。
    if (!muted && unlocked && !bgmSource) startBgm();
  }

  return {
    unlock,
    playSe,
    playRemovalWobble,
    setMuted,
    get muted() {
      return muted;
    },
  };
}
